import type { TemplateData } from './types';

export function buildPlaceholderData(businessName: string): TemplateData {
  return {
    businessName,
    headline: `Expert ${businessName} Services You Can Count On`,
    aboutText: `At ${businessName}, we bring years of dedicated experience to every project. Our team combines deep industry knowledge with a genuine commitment to each client's success.\n\nFrom the first consultation to the final result, we focus on clear communication, reliable timelines, and work that speaks for itself. Our clients stay with us because we deliver - consistently and without shortcuts.`,
    services: [
      { name: 'Consultation', description: 'Expert guidance tailored to your specific needs and goals.', icon: 'users' },
      { name: 'Custom Solutions', description: 'Bespoke approaches designed for lasting, measurable results.', icon: 'wrench' },
      { name: 'Ongoing Support', description: 'Reliable assistance whenever you need it most.', icon: 'shield' },
      { name: 'Quality Assurance', description: 'Rigorous standards applied at every step of the process.', icon: 'check' },
      { name: 'Fast Delivery', description: 'Efficient turnaround without cutting corners on quality.', icon: 'zap' },
    ],
    testimonials: [
      { name: 'Sarah Mitchell', role: 'Business Owner', content: 'Outstanding work from start to finish. They truly understood our vision and delivered beyond expectations.', rating: 5 },
      { name: 'James Rivera', role: 'Operations Manager', content: 'Professional, responsive, and delivered exactly what we needed - on time and on budget.', rating: 5 },
      { name: 'Priya Patel', role: 'Entrepreneur', content: 'The quality of their work speaks for itself. We saw real results within weeks of launching.', rating: 5 },
    ],
    seo: {
      title: `${businessName} | Professional Services`,
      description: `${businessName} provides trusted, high-quality services. Contact us today to get started.`,
    },
    businessData: {
      phone: '(555) 123-4567',
      rating: 4.8,
      reviewCount: 127,
      businessHours: [
        'Monday: 9:00 AM – 6:00 PM',
        'Tuesday: 9:00 AM – 6:00 PM',
        'Wednesday: 9:00 AM – 6:00 PM',
        'Thursday: 9:00 AM – 6:00 PM',
        'Friday: 9:00 AM – 5:00 PM',
        'Saturday: 10:00 AM – 2:00 PM',
        'Sunday: Closed',
      ],
      photos: [],
      address: '123 Main Street, Suite 100',
      googleMapsUrl: null,
    },
    faq: [
      { question: 'How do I get started?', answer: 'Simply reach out through our contact form or give us a call. We\'ll schedule a free consultation to discuss your needs.' },
      { question: 'What areas do you serve?', answer: 'We proudly serve our local community and surrounding areas. Contact us for details about your specific location.' },
      { question: 'How long does a typical project take?', answer: 'Most projects are completed within 2-4 weeks, depending on scope and complexity.' },
      { question: 'Do you offer free estimates?', answer: 'Yes! We provide detailed, no-obligation estimates for all of our services.' },
      { question: 'What sets you apart from competitors?', answer: 'Our combination of experience, attention to detail, and commitment to client satisfaction makes us the preferred choice.' },
    ],
    priceList: [
      { name: 'Basic Package', description: 'Essential services for small projects', price: 'From $99' },
      { name: 'Standard Package', description: 'Comprehensive solution for growing needs', price: 'From $249' },
      { name: 'Premium Package', description: 'Full-service experience with priority support', price: 'From $499' },
      { name: 'Custom Quote', description: 'Tailored solutions for unique requirements', price: 'Contact Us' },
    ],
    promotion: {
      title: 'Spring Special',
      description: 'Book this month and save on your first project',
      discount: '15% Off',
    },
  };
}

// Diverse fictional businesses for template preview cards.
// Each template gets a unique placeholder so the selection screen feels varied.
const PREVIEW_BUSINESSES: {
  name: string;
  headline: string;
  about: string;
  services: { name: string; description: string; icon: string }[];
  testimonials: { name: string; role: string; content: string; rating: number }[];
  photos: string[];
}[] = [
  {
    name: 'Riverside Bakery',
    headline: 'Fresh-Baked Goodness, Every Morning',
    about: 'Riverside Bakery has been a neighborhood staple for over a decade. We craft artisan breads, pastries, and cakes using locally sourced ingredients and time-honored recipes.',
    services: [
      { name: 'Artisan Breads', description: 'Sourdough, rye, and ciabatta baked fresh daily.', icon: 'heart' },
      { name: 'Custom Cakes', description: 'Beautifully decorated cakes for any occasion.', icon: 'star' },
      { name: 'Pastry Boxes', description: 'Curated assortments perfect for gifts and events.', icon: 'check' },
      { name: 'Catering', description: 'Breads and sweets for your next gathering.', icon: 'users' },
    ],
    testimonials: [
      { name: 'Alice W.', role: 'Local Resident', content: 'The croissants here are the best I\'ve ever had - flaky, buttery, and perfect every time.', rating: 5 },
      { name: 'Tom R.', role: 'Event Planner', content: 'Our wedding cake was stunning. Guests are still talking about it months later.', rating: 5 },
      { name: 'Maria S.', role: 'Café Owner', content: 'We stock their sourdough in our shop - our customers love it.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=800&q=80',
      'https://images.unsplash.com/photo-1555507036-ab1f4038024a?w=800&q=80',
      'https://images.unsplash.com/photo-1517433670267-08bbd4be890f?w=800&q=80',
    ],
  },
  {
    name: 'Summit Fitness',
    headline: 'Push Your Limits, Reach Your Peak',
    about: 'Summit Fitness is a modern gym built for everyone - from first-timers to competitive athletes. Our certified trainers and top-tier equipment help you reach goals you never thought possible.',
    services: [
      { name: 'Personal Training', description: 'One-on-one coaching tailored to your goals.', icon: 'users' },
      { name: 'Group Classes', description: 'High-energy classes from yoga to HIIT.', icon: 'zap' },
      { name: 'Nutrition Plans', description: 'Custom meal plans to fuel your progress.', icon: 'heart' },
      { name: 'Recovery Zone', description: 'Sauna, cold plunge, and stretching area.', icon: 'shield' },
    ],
    testimonials: [
      { name: 'David K.', role: 'Member', content: 'I\'ve been to a lot of gyms - Summit is on another level. The trainers actually care.', rating: 5 },
      { name: 'Jenny L.', role: 'Marathon Runner', content: 'The recovery zone alone is worth the membership. My performance has improved dramatically.', rating: 5 },
      { name: 'Chris B.', role: 'New Member', content: 'I was nervous to start, but the staff made me feel welcome from day one.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80',
      'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&q=80',
      'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80',
    ],
  },
  {
    name: 'Greenleaf Landscaping',
    headline: 'Beautiful Outdoor Spaces, Built to Last',
    about: 'Greenleaf Landscaping transforms yards into retreats. From design to maintenance, our team brings creativity, craftsmanship, and a deep love of the outdoors to every project.',
    services: [
      { name: 'Garden Design', description: 'Custom layouts that complement your home.', icon: 'home' },
      { name: 'Lawn Maintenance', description: 'Weekly care to keep your yard pristine.', icon: 'check' },
      { name: 'Hardscaping', description: 'Patios, walkways, and retaining walls.', icon: 'wrench' },
      { name: 'Tree Services', description: 'Pruning, removal, and planting.', icon: 'shield' },
    ],
    testimonials: [
      { name: 'Patricia M.', role: 'Homeowner', content: 'Our backyard looks like something out of a magazine. Couldn\'t be happier.', rating: 5 },
      { name: 'Robert J.', role: 'Property Manager', content: 'Reliable, professional, and always on schedule. We use them for all our properties.', rating: 5 },
      { name: 'Angela F.', role: 'Homeowner', content: 'The patio they built is where we spend every evening now. Truly life-changing.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
      'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&q=80',
      'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80',
    ],
  },
  {
    name: 'Atlas Coffee Roasters',
    headline: 'Specialty Coffee, Roasted with Care',
    about: 'Atlas Coffee Roasters sources beans from small farms around the world and roasts them in small batches right here in town. Every cup tells a story of craftsmanship and connection.',
    services: [
      { name: 'Fresh Roasts', description: 'Single-origin and blend options roasted weekly.', icon: 'zap' },
      { name: 'Subscriptions', description: 'Fresh beans delivered to your door monthly.', icon: 'mail' },
      { name: 'Wholesale', description: 'Premium coffee supply for cafés and offices.', icon: 'users' },
      { name: 'Brewing Workshops', description: 'Learn pour-over, espresso, and cold brew.', icon: 'star' },
    ],
    testimonials: [
      { name: 'Nina T.', role: 'Coffee Enthusiast', content: 'I\'ve tried dozens of roasters - Atlas is my go-to. The Ethiopian blend is exceptional.', rating: 5 },
      { name: 'Leo H.', role: 'Café Owner', content: 'Switching to Atlas was the best decision for our shop. Customers notice the difference.', rating: 5 },
      { name: 'Grace P.', role: 'Subscriber', content: 'The monthly subscription is the highlight of my week. Always something new to try.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=80',
      'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=800&q=80',
      'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=800&q=80',
    ],
  },
  {
    name: 'Bright Pixel Studio',
    headline: 'Creative Design That Makes an Impact',
    about: 'Bright Pixel Studio is a boutique design agency specializing in branding, web design, and visual storytelling. We help businesses stand out with bold, thoughtful creative work.',
    services: [
      { name: 'Brand Identity', description: 'Logos, color systems, and brand guidelines.', icon: 'star' },
      { name: 'Web Design', description: 'Beautiful, responsive websites that convert.', icon: 'wrench' },
      { name: 'Print Design', description: 'Business cards, posters, and packaging.', icon: 'check' },
      { name: 'Social Media', description: 'Scroll-stopping visuals for your channels.', icon: 'zap' },
    ],
    testimonials: [
      { name: 'Ethan C.', role: 'Startup Founder', content: 'They completely transformed our brand. We went from invisible to unforgettable.', rating: 5 },
      { name: 'Mia K.', role: 'Marketing Director', content: 'Fast, creative, and a joy to work with. Our best agency partnership yet.', rating: 5 },
      { name: 'Oliver D.', role: 'Restaurant Owner', content: 'The new menu design and website brought in so many new customers.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&q=80',
      'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80',
      'https://images.unsplash.com/photo-1542744094-3a31f272c490?w=800&q=80',
    ],
  },
  {
    name: 'Harmony Wellness Spa',
    headline: 'Relax, Restore, Renew',
    about: 'Harmony Wellness Spa offers a sanctuary from the everyday. Our skilled therapists combine ancient techniques with modern wellness practices to help you feel your absolute best.',
    services: [
      { name: 'Massage Therapy', description: 'Deep tissue, Swedish, and hot stone options.', icon: 'heart' },
      { name: 'Facials', description: 'Customized skincare treatments for radiant skin.', icon: 'star' },
      { name: 'Body Treatments', description: 'Wraps, scrubs, and aromatherapy sessions.', icon: 'shield' },
      { name: 'Couples Packages', description: 'Shared experiences for a perfect getaway.', icon: 'users' },
    ],
    testimonials: [
      { name: 'Sophie A.', role: 'Regular Client', content: 'My monthly massage here is non-negotiable. The best therapists in the city.', rating: 5 },
      { name: 'James F.', role: 'First-Time Visitor', content: 'I went in stressed and came out a completely different person. Incredible experience.', rating: 5 },
      { name: 'Lena V.', role: 'Bride-to-Be', content: 'The bridal package was perfect - my whole party left glowing. Thank you!', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
      'https://images.unsplash.com/photo-1540555700478-4be289fbec6d?w=800&q=80',
      'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=800&q=80',
    ],
  },
  {
    name: 'Ironworks Auto',
    headline: 'Honest Repairs, Expert Craftsmanship',
    about: 'Ironworks Auto is a family-owned shop that\'s been keeping vehicles on the road for over 25 years. We believe in transparent pricing, quality parts, and getting the job done right the first time.',
    services: [
      { name: 'Diagnostics', description: 'State-of-the-art computer diagnostics.', icon: 'wrench' },
      { name: 'Brake Service', description: 'Pads, rotors, and full brake system repairs.', icon: 'shield' },
      { name: 'Oil Changes', description: 'Quick service with premium synthetic oils.', icon: 'zap' },
      { name: 'Tire Shop', description: 'New tires, rotations, and alignments.', icon: 'check' },
    ],
    testimonials: [
      { name: 'Mike D.', role: 'Long-Time Customer', content: 'I\'ve taken every car I\'ve owned here for 15 years. Wouldn\'t trust anyone else.', rating: 5 },
      { name: 'Karen W.', role: 'New Customer', content: 'They were honest about what I actually needed - no upselling. Refreshing.', rating: 5 },
      { name: 'Tyler G.', role: 'Car Enthusiast', content: 'Great work on my classic restoration. These guys know what they\'re doing.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=800&q=80',
      'https://images.unsplash.com/photo-1625047509248-ec889cbff17f?w=800&q=80',
      'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80',
    ],
  },
  {
    name: 'Oakwood Law Group',
    headline: 'Legal Guidance You Can Trust',
    about: 'Oakwood Law Group provides experienced legal counsel for individuals and businesses. We combine deep expertise with a client-first approach to deliver results that matter.',
    services: [
      { name: 'Business Law', description: 'Contracts, formation, and compliance.', icon: 'shield' },
      { name: 'Estate Planning', description: 'Wills, trusts, and asset protection.', icon: 'home' },
      { name: 'Real Estate', description: 'Closings, disputes, and title review.', icon: 'check' },
      { name: 'Litigation', description: 'Skilled courtroom representation.', icon: 'star' },
    ],
    testimonials: [
      { name: 'Richard B.', role: 'Business Owner', content: 'They handled our contract dispute swiftly and professionally. Excellent outcome.', rating: 5 },
      { name: 'Ellen M.', role: 'Client', content: 'Estate planning felt overwhelming until I met with their team. So thorough and kind.', rating: 5 },
      { name: 'Sam Y.', role: 'Real Estate Investor', content: 'I rely on Oakwood for every deal. They catch things other firms miss.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80',
      'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
    ],
  },
  {
    name: 'Wildflower Florist',
    headline: 'Blooms That Brighten Every Moment',
    about: 'Wildflower Florist crafts stunning arrangements using seasonal, locally grown flowers. Whether it\'s a simple bouquet or a grand event display, we bring natural beauty to every occasion.',
    services: [
      { name: 'Custom Bouquets', description: 'Handcrafted arrangements for any occasion.', icon: 'heart' },
      { name: 'Wedding Flowers', description: 'From centerpieces to bridal bouquets.', icon: 'star' },
      { name: 'Weekly Delivery', description: 'Fresh flowers for your home or office.', icon: 'mail' },
      { name: 'Event Styling', description: 'Full floral design for special events.', icon: 'users' },
    ],
    testimonials: [
      { name: 'Hannah L.', role: 'Bride', content: 'My wedding flowers were absolutely breathtaking. Every arrangement was perfect.', rating: 5 },
      { name: 'Derek N.', role: 'Regular Customer', content: 'I order weekly bouquets for our office - they always brighten up the space.', rating: 5 },
      { name: 'Yuki S.', role: 'Event Host', content: 'The centerpieces at our gala were the talk of the evening. Stunning work.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1487530811176-3780de880c2d?w=800&q=80',
      'https://images.unsplash.com/photo-1490750967868-88aa4f44baee?w=800&q=80',
      'https://images.unsplash.com/photo-1455659817273-f96807779a8a?w=800&q=80',
    ],
  },
  {
    name: 'Apex Tech Solutions',
    headline: 'Smart IT Solutions for Growing Businesses',
    about: 'Apex Tech Solutions provides managed IT services, cloud infrastructure, and cybersecurity for small and mid-sized businesses. We keep your technology running so you can focus on what you do best.',
    services: [
      { name: 'Managed IT', description: '24/7 monitoring and support for your systems.', icon: 'shield' },
      { name: 'Cloud Services', description: 'Migration, hosting, and optimization.', icon: 'zap' },
      { name: 'Cybersecurity', description: 'Protect your data from modern threats.', icon: 'wrench' },
      { name: 'Consulting', description: 'Strategic planning for your IT roadmap.', icon: 'users' },
    ],
    testimonials: [
      { name: 'Marcus T.', role: 'CEO', content: 'Since switching to Apex, our downtime has dropped to nearly zero. Game-changer.', rating: 5 },
      { name: 'Rachel P.', role: 'Office Manager', content: 'Their support team is incredibly responsive. Issues get resolved in minutes, not days.', rating: 5 },
      { name: 'Kevin Z.', role: 'CTO', content: 'The cloud migration was seamless. Our team didn\'t miss a beat.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80',
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
      'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80',
    ],
  },
  {
    name: 'Blue Anchor Dental',
    headline: 'Gentle Care, Healthy Smiles',
    about: 'Blue Anchor Dental provides comprehensive dental care in a warm, welcoming environment. From routine cleanings to cosmetic procedures, our team is dedicated to keeping your smile bright.',
    services: [
      { name: 'Cleanings & Exams', description: 'Preventive care for lasting oral health.', icon: 'check' },
      { name: 'Cosmetic Dentistry', description: 'Whitening, veneers, and smile makeovers.', icon: 'star' },
      { name: 'Restorative', description: 'Fillings, crowns, and implants.', icon: 'wrench' },
      { name: 'Emergency Care', description: 'Same-day appointments when you need them.', icon: 'zap' },
    ],
    testimonials: [
      { name: 'Laura C.', role: 'Patient', content: 'I used to dread the dentist, but this team makes every visit comfortable and easy.', rating: 5 },
      { name: 'Brandon E.', role: 'Parent', content: 'My kids actually look forward to their appointments here. That says it all.', rating: 5 },
      { name: 'Diana Q.', role: 'Patient', content: 'The whitening results were amazing. I can\'t stop smiling!', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&q=80',
      'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=800&q=80',
      'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=800&q=80',
    ],
  },
  {
    name: 'Starlight Photography',
    headline: 'Capturing Moments That Last Forever',
    about: 'Starlight Photography specializes in portraits, events, and commercial photography. We blend artistic vision with technical precision to create images that tell your story beautifully.',
    services: [
      { name: 'Portraits', description: 'Individual, family, and professional headshots.', icon: 'users' },
      { name: 'Events', description: 'Weddings, corporate events, and celebrations.', icon: 'star' },
      { name: 'Commercial', description: 'Product and brand photography.', icon: 'check' },
      { name: 'Photo Editing', description: 'Professional retouching and color grading.', icon: 'wrench' },
    ],
    testimonials: [
      { name: 'Ashley R.', role: 'Newlywed', content: 'Our wedding photos are absolutely magical. Every moment was captured perfectly.', rating: 5 },
      { name: 'Ben W.', role: 'Business Owner', content: 'The product shots they did for our website tripled our conversion rate.', rating: 5 },
      { name: 'Chloe M.', role: 'Senior Portrait', content: 'I felt so natural during the shoot and the results are gorgeous.', rating: 5 },
    ],
    photos: [
      'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=800&q=80',
      'https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?w=800&q=80',
      'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=800&q=80',
    ],
  },
];

/**
 * Returns unique placeholder data for each template preview card.
 * Uses a stable mapping from template ID to a fictional business
 * so that the template selection screen shows diverse, clearly
 * generic content instead of the user's actual business info.
 */
export function buildTemplatePreviewData(templateId: string): TemplateData {
  // Simple hash to get a stable index per template ID
  let hash = 0;
  for (let i = 0; i < templateId.length; i++) {
    hash = ((hash << 5) - hash + templateId.charCodeAt(i)) | 0;
  }
  const biz = PREVIEW_BUSINESSES[Math.abs(hash) % PREVIEW_BUSINESSES.length];

  return {
    businessName: biz.name,
    headline: biz.headline,
    aboutText: biz.about,
    services: biz.services,
    testimonials: biz.testimonials,
    seo: {
      title: `${biz.name} | Welcome`,
      description: biz.about.slice(0, 155),
    },
    businessData: {
      phone: '(555) 000-0000',
      rating: 4.9,
      reviewCount: 86,
      businessHours: [
        'Monday – Friday: 9 AM – 6 PM',
        'Saturday: 10 AM – 4 PM',
        'Sunday: Closed',
      ],
      photos: biz.photos,
      address: '456 Elm Street, Downtown',
      googleMapsUrl: null,
    },
    faq: [
      { question: 'Do you offer free consultations?', answer: 'Yes - reach out anytime to schedule a no-obligation consultation.' },
      { question: 'What are your hours?', answer: 'We\'re open Monday through Friday, 9 AM to 6 PM, and Saturdays 10 AM to 4 PM.' },
      { question: 'Where are you located?', answer: 'We\'re conveniently located in the heart of downtown. Visit our contact page for directions.' },
    ],
    priceList: [
      { name: 'Starter', description: 'Great for getting started', price: 'From $75' },
      { name: 'Standard', description: 'Our most popular option', price: 'From $150' },
      { name: 'Premium', description: 'The full experience', price: 'From $300' },
    ],
    promotion: {
      title: 'New Client Special',
      description: 'First-time visitors save on their initial visit',
      discount: '20% Off',
    },
  };
}
